<template>
  <Card variant="outline-secondary" size="medium">
    <div class="strip">
      <IconTile :icon="scope.icon" :color="scope.tone" size="small" shape="rounded" colored-icon />

      <div class="text">
        <Text variant="label-medium">{{ scope.headline }}</Text>

        <Text v-if="scope.body" variant="body-small" color="secondary">{{ scope.body }}</Text>

        <!-- Gap only, no separators, so a wrapped line never starts with a dangling dot -->
        <div v-else class="meta">
          <template v-if="scope.kind === 'portfolio'">
            <span class="segment">
              <Avatar v-if="owner" :user-name="nameOf(owner)" show-single-letter size="small" />
              <Text as="span" variant="body-small" color="secondary">{{ ownedBy }}</Text>
            </span>
            <span class="segment">
              <CollaboratorAvatars v-if="collaboratorNames.length" :names="collaboratorNames" />
              <Text as="span" variant="body-small" color="secondary">{{ collaboratorsLabel }}</Text>
            </span>
            <Text as="span" variant="body-small" color="secondary">{{ countLabel }}</Text>
          </template>

          <template v-else>
            <Text v-for="line in summaryLines" :key="line" as="span" variant="body-small" color="secondary">
              {{ line }}
            </Text>
          </template>
        </div>
      </div>

      <div class="actions">
        <Button v-if="scope.primary" size="medium" class="primary-action" @click="firePrimary(scope.primary.event)">
          <Icon :name="scope.primary.icon" size="small" />
          <span>{{ scope.primary.label }}</span>
        </Button>

        <PortfolioSwitchMenu
          v-if="portfolios.length"
          v-model="model"
          :portfolios="portfolios"
          :counts="counts"
          :total="total"
          full-width
          @manage="emit('manage')"
          @create="emit('create')"
        >
          <template #trigger>
            <Button variant="outline" size="medium" tabindex="-1" class="switch-button">
              <Icon name="switch-horizontal-01" size="small" />
              <span>Switch portfolio</span>
            </Button>
          </template>
        </PortfolioSwitchMenu>

        <!-- A ghost has no edge to fill half a phone row, so it firms up to an outline there -->
        <Button v-if="canManage" :variant="secondaryVariant" size="medium" @click="emit('manage')">
          <Icon name="settings-01" size="small" />
          <span>Manage</span>
        </Button>

        <Button v-if="scope.kind === 'invite'" :variant="secondaryVariant" size="medium" @click="emit('dismiss')">
          Not now
        </Button>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed } from 'vue';

import {
  Avatar,
  Button,
  Card,
  Icon,
  IconName,
  IconTile,
  IconTileColor,
  Text,
  screenMobile,
} from '@tallo/design-system';
import {
  CollaboratorAvatars,
  CompanyTeamMemberDto,
  PortfolioDto,
  teamMemberName as nameOf,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  Holdings,
  PortfolioFilter,
  holdingsLabel,
  holdingsTotal,
  noHoldings,
  unassignedPortfolioFilter,
} from '@tallo/investor/property';

import PortfolioSwitchMenu from './portfolio-switch-menu.vue';

type PrimaryEvent = 'create' | 'file' | 'add';

interface Scope {
  kind: 'invite' | 'all' | 'unassigned' | 'portfolio';
  icon: IconName;
  tone: IconTileColor;
  headline: string;
  /** A sentence in place of the meta line */
  body?: string;
  primary?: { event: PrimaryEvent; label: string; icon: IconName };
}

const props = defineProps<{
  portfolios: PortfolioDto[];
  members: CompanyTeamMemberDto[];
  /** What each entry holds, keyed like the filter: a portfolio id or `unassigned` */
  counts: Record<string, Holdings>;
  total: Holdings;
  /** Whether the viewer may file at least one of the unassigned properties: an admin, or the manager of one */
  canFile: boolean;
}>();

/** Which portfolio the page is showing */
const model = defineModel<PortfolioFilter>({ required: true });
const emit = defineEmits<{ manage: []; create: []; file: []; add: []; dismiss: [] }>();

// Past this many characters a name in the summary line crowds out the others
const summaryNameLength = 32;

const { investor, isAdmin } = storeToRefs(useInvestorStore());

const selected = computed(() => props.portfolios.find((portfolio) => portfolio.id === model.value));
const unassignedCount = computed(() => holdingsTotal(props.counts[unassignedPortfolioFilter] ?? noHoldings));
const selectedHoldings = computed(() => (selected.value && props.counts[selected.value.id]) || noHoldings);
const selectedCount = computed(() => holdingsTotal(selectedHoldings.value));

const owner = computed(() => props.members.find((member) => member.investorId === selected.value?.ownerId));
const isOwner = computed(() => Boolean(owner.value?.isYou));
// Nothing to manage until a portfolio exists, so the invitation offers only its own two answers
const canManage = computed(() => props.portfolios.length > 0 && (isAdmin.value || isOwner.value));

const collaborators = computed(() => selected.value?.collaborators ?? []);
// The faces are the portfolio's default collaborators, the ones who join every unit filed into it
const collaboratorNames = computed(() =>
  collaborators.value
    .filter((collaborator) => collaborator.joinsNewUnits)
    .map((collaborator) => props.members.find((member) => member.investorId === collaborator.investorId))
    .filter((member): member is CompanyTeamMemberDto => Boolean(member))
    .map(nameOf),
);
const youCollaborate = computed(
  () => !isOwner.value && collaborators.value.some(({ investorId }) => investorId === investor.value?.id),
);

const scope = computed<Scope>(() => {
  if (!props.portfolios.length) {
    return {
      kind: 'invite',
      icon: 'folder',
      tone: 'orange',
      headline: 'Group your properties into portfolios',
      body: isAdmin.value
        ? 'Hand a set of properties to a teammate who then runs everything inside. Everything stays here under Your properties.'
        : 'Keep the properties you run together in a portfolio of your own. Everything stays here under Your properties.',
      primary: { event: 'create', label: 'Create a portfolio', icon: 'plus' },
    };
  }

  if (selected.value) {
    const count = selectedCount.value;

    return {
      kind: 'portfolio',
      icon: 'folder',
      tone: 'neutral',
      headline:
        props.portfolios.length === 1
          ? 'Your only portfolio'
          : `You're viewing 1 of ${props.portfolios.length} portfolios`,
      primary: count === 0 && canManage.value ? { event: 'add', label: 'Add properties', icon: 'plus' } : undefined,
    };
  }

  if (model.value === unassignedPortfolioFilter) {
    const count = unassignedCount.value;

    if (count === 0) {
      return {
        kind: 'unassigned',
        icon: 'check',
        tone: 'neutral',
        headline: 'Every property is in a portfolio',
        body: 'New listings made outside a portfolio land here.',
      };
    }

    return {
      kind: 'unassigned',
      icon: 'home-03',
      tone: 'neutral',
      headline: `${count} ${count === 1 ? "property isn't" : "properties aren't"} in any portfolio`,
      body: 'They keep the manager they have. File them into a portfolio to hand them to an owner.',
      primary: props.canFile ? { event: 'file', label: 'File into a portfolio', icon: 'folder' } : undefined,
    };
  }

  return {
    kind: 'all',
    icon: 'grid-01',
    tone: 'neutral',
    headline: `Everything across ${pluralize('portfolio', props.portfolios.length, true)}`,
  };
});

const ownedBy = computed(() => {
  if (!owner.value) {
    return 'No owner';
  }

  return owner.value.isYou ? 'Owned by you' : `Owned by ${nameOf(owner.value)}`;
});

const collaboratorsLabel = computed(() =>
  collaboratorNames.value.length
    ? pluralize('collaborator', collaboratorNames.value.length, true)
    : 'No collaborators yet',
);

const countLabel = computed(() => {
  const label = holdingsLabel(selectedHoldings.value);

  return youCollaborate.value ? `${label} · you collaborate` : label;
});

/** The summary behind "All properties": every portfolio with its size, then the loose ones */
const summaryLines = computed(() => [
  ...props.portfolios.map(
    (portfolio) => `${shorten(portfolio.name)} · ${holdingsTotal(props.counts[portfolio.id] ?? noHoldings)}`,
  ),
  `Unassigned · ${unassignedCount.value}`,
]);

// On a phone the secondary buttons share a row, where a ghost has no edge to fill; an outline does
const secondaryVariant = computed(() => (screenMobile.value ? 'outline' : 'ghost'));

/** The one primary action per state, named by the state itself */
function firePrimary(event: PrimaryEvent) {
  switch (event) {
    case 'create':
      emit('create');
      break;

    case 'file':
      emit('file');
      break;

    case 'add':
      emit('add');
      break;
  }
}

function shorten(name: string): string {
  return name.length > summaryNameLength ? `${name.slice(0, summaryNameLength - 1).trimEnd()}…` : name;
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.strip {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-lg);
}

.text {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-xs);
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: var(--t-spacing-xs);
  column-gap: 0.75rem;
}

.segment {
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-sm);
}

// The buttons keep their size; the text block gives way
.actions {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-sm);
  flex-shrink: 0;
}

.switch-button {
  width: 100%;
}

@include mobile {
  .strip {
    flex-direction: column;
    align-items: stretch;
    gap: var(--t-spacing-md);
  }

  .text {
    // The tile sits beside the text, the actions under both
    padding-left: calc(2.25rem + 0.75rem);
    margin-top: calc(-1 * (2.25rem + var(--t-spacing-md)));
    min-height: 2.25rem;
    justify-content: center;
  }

  // One button takes the row; two share it; a primary action takes a row of its own above the pair
  .actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > * {
      width: 100%;
    }

    > .primary-action,
    > :only-child {
      grid-column: 1 / -1;
    }

    // Half a phone row is tight for an icon and a label: less air inside, and the label is cut before it spills
    .button {
      min-width: 0;
      gap: var(--t-spacing-xs);
      padding-inline: var(--t-spacing-xs);

      span {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
